<template>
    <div class="bg-white rounded-lg shadow p-6">
      <h2 class="text-xl font-semibold mb-6">Payment Methods</h2>
  
      <!-- Add Payment Method -->
      <div class="mb-8 p-4 border border-dashed border-gray-300 rounded-lg">
        <h3 class="text-lg font-medium mb-4">Add Payment Method</h3>
  
        <div ref="cardEl" class="mb-4 p-3 border rounded"></div>
        <p ref="cardErrorEl" class="text-red-500 text-sm mb-4"></p>
  
        <button
          @click="addPaymentMethod"
          :disabled="isAdding || !stripe || !cardElement"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="isAdding">Adding...</span>
          <span v-else>Add Payment Method</span>
        </button>
      </div>
  
      <!-- Payment Methods List -->
      <div v-if="listLoading">
        <p class="text-gray-500">Loading your payment methods…</p>
      </div>
  
      <div v-else-if="paymentMethods.length > 0">
        <h3 class="text-lg font-medium mb-4">Your Payment Methods</h3>
        <div class="space-y-4">
          <div
            v-for="method in paymentMethods"
            :key="method.id"
            class="flex items-center justify-between p-4 border rounded-lg"
            :class="{ 'border-blue-500 bg-blue-50': method.is_default }"
          >
            <div class="flex items-center">
              <i :class="getCardIcon(method.brand)" class="text-2xl mr-4"></i>
              <div>
                <p class="font-medium">•••• •••• •••• {{ method.last4 }}</p>
                <p class="text-sm text-gray-500">
                  {{ titleCase(method.brand) }} • Expires {{ pad2(method.exp_month) }}/{{ method.exp_year }}
                  <span
                    v-if="method.is_default"
                    class="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                  >Default</span>
                </p>
              </div>
            </div>
            <div class="flex space-x-3">
              <button
                v-if="!method.is_default"
                @click="setDefaultPaymentMethod(method.id)"
                :disabled="isSettingDefault === method.id"
                class="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                <span v-if="isSettingDefault === method.id">Setting…</span>
                <span v-else>Set as Default</span>
              </button>
              <button
                @click="confirmDelete(method.id)"
                :disabled="isDeleting === method.id"
                class="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                <span v-if="isDeleting === method.id">Deleting…</span>
                <span v-else>Remove</span>
              </button>
            </div>
          </div>
        </div>
      </div>
  
      <div v-else class="text-gray-500">
        No payment methods found.
      </div>
    </div>
  </template>
  
  <script>
  import { loadStripe } from '@stripe/stripe-js';
  
  export default {
    name: 'PaymentMethods',
  
    data() {
      return {
        stripeKey: window.STRIPE_PUBLISHABLE_KEY,
  
        // Stripe
        stripe: null,
        elements: null,
        cardElement: null,
  
        // UI state
        listLoading: true,
        isAdding: false,
        isSettingDefault: null,
        isDeleting: null,
  
        // Data
        paymentMethods: [],
      };
    },
  
    async mounted() {
      try {
        await this.initStripe();
      } catch (e) {
        console.error('Stripe init failed:', e);
        this.$toast?.error?.(e.message || 'Failed to initialize payment form.');
      }
      await this.refreshPaymentMethods(); // load list regardless of Stripe status
    },
  
    beforeUnmount() {
      try {
        this.cardElement?.unmount?.();
        this.cardElement?.destroy?.();
      } catch {
      // Teardown must not throw: the element may already be unmounted.
    }
      this.cardElement = null;
      this.elements = null;
    },
  
    methods: {
      /** ------------ Helpers ------------ */
      unwrap(res) {
        // Accepts:
        // - axios resp: { data: { success, data } }
        // - axios resp (direct array): { data: [...] }
        // - direct payload: [...]
        return res?.data?.data ?? res?.data ?? res ?? null;
      },
      titleCase(s) {
        if (!s) return '';
        return String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase();
      },
      pad2(n) {
        const v = Number(n || 0);
        return v < 10 ? `0${v}` : String(v);
      },
      getCardIcon(brand) {
        const icons = {
          visa: 'fab fa-cc-visa',
          mastercard: 'fab fa-cc-mastercard',
          amex: 'fab fa-cc-amex',
          discover: 'fab fa-cc-discover',
          jcb: 'fab fa-cc-jcb',
          diners: 'fab fa-cc-diners-club',
          unionpay: 'fab fa-cc-discover', // fallback
        };
        return icons[String(brand || '').toLowerCase()] || 'far fa-credit-card';
      },
  
      /** ------------ Stripe init ------------ */
      async initStripe() {
        if (!this.stripeKey) {
          throw new Error('Stripe publishable key missing.');
        }
  
        this.stripe = await loadStripe(this.stripeKey);
        if (!this.stripe) {
          throw new Error('Failed to initialize Stripe.');
        }
  
        this.elements = this.stripe.elements();
        this.cardElement = this.elements.create('card');
  
        await this.$nextTick();
        if (!this.$refs.cardEl) {
          throw new Error('Card element mount target not found.');
        }
  
        this.cardElement.mount(this.$refs.cardEl);
        this.cardElement.on('change', (event) => {
          if (this.$refs.cardErrorEl) {
            this.$refs.cardErrorEl.textContent = event.error ? event.error.message : '';
          }
        });
      },
  
      /** ------------ API calls ------------ */
      async refreshPaymentMethods() {
        this.listLoading = true;
        try {
          const res = await this.$store.dispatch('billing/fetchPaymentMethods');
          const list = this.unwrap(res) || [];
          // Expect items like: { id, brand, last4, exp_month, exp_year, is_default }
          this.paymentMethods = Array.isArray(list) ? list : [];
        } catch (e) {
          console.error(e);
          this.$toast?.error?.('Failed to load payment methods.');
          this.paymentMethods = [];
        } finally {
          this.listLoading = false;
        }
      },
  
      async addPaymentMethod() {
        if (!this.stripe || !this.cardElement) return;
        this.isAdding = true;
        try {
          // Create SetupIntent on backend
          const siRes = await this.$store.dispatch('billing/createSetupIntent');
          const setupIntent = this.unwrap(siRes);
          const clientSecret = setupIntent?.client_secret || setupIntent?.setup_intent?.client_secret;
          if (!clientSecret) {
            throw new Error('Missing client secret for Setup Intent.');
          }
  
          const { error } = await this.stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: this.cardElement,
              // Optional: attach name/email here if you have them available globally
              // billing_details: { name: this.$page?.props?.auth?.user?.name }
            },
          });
  
          if (error) throw error;
  
          // Refresh list and clear card input
          await this.refreshPaymentMethods();
          this.cardElement.clear?.();
          this.$toast?.success?.('Payment method added successfully.');
        } catch (e) {
          console.error(e);
          this.$toast?.error?.(e?.message || 'Failed to add payment method.');
        } finally {
          this.isAdding = false;
        }
      },
  
      async setDefaultPaymentMethod(paymentMethodId) {
        this.isSettingDefault = paymentMethodId;
        try {
          await this.$store.dispatch('billing/setDefaultPaymentMethod', paymentMethodId);
          await this.refreshPaymentMethods();
          this.$toast?.success?.('Default payment method updated.');
        } catch (e) {
          console.error(e);
          this.$toast?.error?.('Failed to update default payment method.');
        } finally {
          this.isSettingDefault = null;
        }
      },
  
      async confirmDelete(paymentMethodId) {
        try {
          const result = await this.$swal({
            title: 'Remove Payment Method',
            text: 'Are you sure you want to remove this payment method?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove it',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
            focusConfirm: false,
            focusCancel: true
          });

          if (!result.value) {
            return; // User cancelled
          }

          try {
            await this.deletePaymentMethod(paymentMethodId);
            this.$toast?.success?.('Payment method removed successfully.');
          } catch (error) {
            console.error('Error in deletePaymentMethod:', error);
            const errorMessage = error.response?.data?.message || 'Failed to remove payment method. Please try again.';
            this.$toast?.error?.(errorMessage);
            
            // Refresh payment methods to ensure UI is in sync
            try {
              await this.$store.dispatch('billing/fetchPaymentMethods');
            } catch (refreshError) {
              console.error('Failed to refresh payment methods:', refreshError);
            }
          }
        } catch (error) {
          console.error('Error in confirmDelete:', error);
          this.$toast?.error?.('An error occurred while processing your request.');
        }
      },
  
      async deletePaymentMethod(paymentMethodId) {
        this.isDeleting = paymentMethodId;
        try {
          await this.$store.dispatch('billing/deletePaymentMethod', paymentMethodId);
          await this.refreshPaymentMethods();
          this.$toast?.success?.('Payment method removed.');
        } catch (e) {
          console.error(e);
          this.$toast?.error?.('Failed to remove payment method.');
        } finally {
          this.isDeleting = null;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  /* You can keep using FontAwesome classes for brand icons */
  </style>
  